import React, { useState, useCallback } from 'react';
import { CraftedItem } from './Crafting';
import { getItemTypeFromResult } from '../utils/craftingUtils';
import EquipmentIcon from './EquipmentIcon';
import { makeAuthenticatedRequest } from '../utils/api';

interface DismantlePreview {
  itemName: string;
  qualityTier: number;
  materials: Array<{ materialId: number; materialName: string; quantity: number }>;
}

interface TownOption {
  id: number;
  name: string;
  standing: number;
  level: number;
}

type ModalKind = 'dismantle' | 'combine' | 'donate';

interface ModalState {
  kind: ModalKind;
  sourceItem: CraftedItem;
  // dismantle
  preview: DismantlePreview | null;
  // combine
  combineTarget: CraftedItem | null;
  // donate
  towns: TownOption[];
  selectedTownId: number | null;
  loading: boolean;
}

interface Props {
  craftedItems: CraftedItem[];
  onDismantle: (itemId: number) => Promise<void>;
  onCombine: (itemId1: number, itemId2: number) => Promise<void>;
  onDonate: (itemId: number, townId: number) => Promise<void>;
}

function qualityLabel(tier: number): string {
  if (tier === 0) return '';
  return ` +${tier}`;
}

function qualityBadgeClass(tier: number): string {
  if (tier >= 5) return 'cf-badge cf-badge--amber';
  if (tier >= 3) return 'cf-badge cf-badge--purple';
  if (tier >= 1) return 'cf-badge cf-badge--blue';
  return '';
}

const STANDING_PER_TIER = 10;
function computeStanding(item: CraftedItem): number {
  return Math.max(10, item.requiredLevel * 5 + item.qualityTier * STANDING_PER_TIER);
}

const CraftingCraftedTab: React.FC<Props> = ({ craftedItems, onDismantle, onCombine, onDonate }) => {
  const [modal, setModal] = useState<ModalState | null>(null);
  const [busy, setBusy] = useState(false);

  const closeModal = useCallback(() => {
    if (!busy) setModal(null);
  }, [busy]);

  const openDismantle = useCallback(async (item: CraftedItem) => {
    const base: ModalState = {
      kind: 'dismantle',
      sourceItem: item,
      preview: null,
      combineTarget: null,
      towns: [],
      selectedTownId: null,
      loading: true
    };
    setModal(base);
    try {
      const res = await makeAuthenticatedRequest(`/crafting/dismantle-preview/${item.id}`);
      if (res.ok) {
        const preview: DismantlePreview = await res.json();
        setModal(prev => prev ? { ...prev, preview, loading: false } : null);
      } else {
        setModal(prev => prev ? { ...prev, loading: false } : null);
      }
    } catch {
      setModal(prev => prev ? { ...prev, loading: false } : null);
    }
  }, []);

  const openCombine = useCallback((item: CraftedItem) => {
    setModal({
      kind: 'combine',
      sourceItem: item,
      preview: null,
      combineTarget: null,
      towns: [],
      selectedTownId: null,
      loading: false
    });
  }, []);

  const openDonate = useCallback(async (item: CraftedItem) => {
    const base: ModalState = {
      kind: 'donate',
      sourceItem: item,
      preview: null,
      combineTarget: null,
      towns: [],
      selectedTownId: null,
      loading: true
    };
    setModal(base);
    try {
      const res = await makeAuthenticatedRequest('/towns');
      if (res.ok) {
        const all: Array<{ id: number; name: string; standing: number; level: number; isLiberated: boolean }> = await res.json();
        const liberated = all
          .filter(t => t.isLiberated)
          .map(t => ({ id: t.id, name: t.name, standing: t.standing, level: t.level }));
        setModal(prev => prev ? { ...prev, towns: liberated, selectedTownId: liberated[0]?.id ?? null, loading: false } : null);
      } else {
        setModal(prev => prev ? { ...prev, loading: false } : null);
      }
    } catch {
      setModal(prev => prev ? { ...prev, loading: false } : null);
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!modal || busy) return;
    setBusy(true);
    try {
      if (modal.kind === 'dismantle') {
        await onDismantle(modal.sourceItem.id);
      } else if (modal.kind === 'combine' && modal.combineTarget) {
        await onCombine(modal.sourceItem.id, modal.combineTarget.id);
      } else if (modal.kind === 'donate' && modal.selectedTownId !== null) {
        await onDonate(modal.sourceItem.id, modal.selectedTownId);
      }
      setModal(null);
    } finally {
      setBusy(false);
    }
  }, [modal, busy, onDismantle, onCombine, onDonate]);

  const combineCompatible = modal?.kind === 'combine'
    ? craftedItems.filter(
        ci => ci.id !== modal.sourceItem.id &&
              ci.recipeId === modal.sourceItem.recipeId &&
              ci.qualityTier === modal.sourceItem.qualityTier &&
              !ci.isEquipped
      )
    : [];

  const confirmEnabled = modal && !busy && !modal.loading && (
    (modal.kind === 'dismantle' && !!modal.preview) ||
    (modal.kind === 'combine' && !!modal.combineTarget) ||
    (modal.kind === 'donate' && modal.selectedTownId !== null)
  );

  return (
    <div className="crafting-tab-content">
      <div className="crafting-section-header crafting-section-header--gold">
        Crafted Items
      </div>

      {craftedItems.length === 0 ? (
        <div className="crafting-empty">
          <p>No items crafted yet</p>
          <small>Craft your first item using discovered recipes</small>
        </div>
      ) : (
        <div className="crafting-crafted-list">
          {craftedItems.map(item => {
            const durabilityPct = (item.durability / item.maxDurability) * 100;
            return (
              <div key={item.id} className={`crafting-crafted-row ${item.isEquipped ? 'equipped' : ''}`}>
                <EquipmentIcon
                  imageUrl={item.imageUrl}
                  iconName={item.iconName}
                  itemName={item.itemName}
                  itemType={getItemTypeFromResult(item.itemType)}
                  size="large"
                  showTooltip={true}
                  stats={{
                    attackBonus: item.attackBonus,
                    defenseBonus: item.defenseBonus,
                    hpBonus: item.hpBonus,
                    speedBonus: item.speedBonus
                  }}
                />

                <div className="crafting-crafted-main">
                  <div className="crafting-crafted-name">
                    {item.itemName}{qualityLabel(item.qualityTier)}
                    {item.qualityTier > 0 && (
                      <span className={`${qualityBadgeClass(item.qualityTier)} cf-quality-badge`}>
                        +{item.qualityTier}
                      </span>
                    )}
                  </div>
                  <div className="crafting-crafted-type">{item.itemType}</div>

                  {(item.attackBonus > 0 || item.defenseBonus > 0 || item.hpBonus > 0 || item.speedBonus > 0) && (
                    <div className="crafting-crafted-stats">
                      {item.attackBonus > 0  && <span className="cf-badge cf-badge--red">ATK +{item.attackBonus}</span>}
                      {item.defenseBonus > 0 && <span className="cf-badge cf-badge--blue">DEF +{item.defenseBonus}</span>}
                      {item.hpBonus > 0      && <span className="cf-badge cf-badge--green">HP +{item.hpBonus}</span>}
                      {item.speedBonus > 0   && <span className="cf-badge cf-badge--amber">SPD +{item.speedBonus}</span>}
                    </div>
                  )}

                  <div className="crafting-crafted-durability">
                    <div className="cf-durability-bar">
                      <div className="cf-durability-fill" style={{ width: `${durabilityPct}%` }} />
                    </div>
                    <span className="cf-durability-text">{item.durability}/{item.maxDurability}</span>
                  </div>

                  {item.isEquipped && item.equippedToCharacterName && (
                    <div className="cf-equipped-badge">Equipped — {item.equippedToCharacterName}</div>
                  )}

                  <div className="crafting-crafted-meta">
                    <span>From: {item.recipeName}</span>
                    <span>{new Date(item.craftedAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {!item.isEquipped && (
                  <div className="cf-disposal-actions">
                    <button
                      className="cf-action-btn cf-action-btn--dismantle"
                      onClick={() => openDismantle(item)}
                      title="Dismantle — recover partial crafting materials"
                    >
                      Dismantle
                    </button>
                    {item.qualityTier < 5 && (
                      <button
                        className="cf-action-btn cf-action-btn--combine"
                        onClick={() => openCombine(item)}
                        title="Combine — merge two identical items into a higher-quality version"
                      >
                        Combine
                      </button>
                    )}
                    <button
                      className="cf-action-btn cf-action-btn--donate"
                      onClick={() => openDonate(item)}
                      title="Donate — contribute to a liberated town for standing"
                    >
                      Donate
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Confirmation Modal ── */}
      {modal && (
        <div className="cf-modal-overlay" onClick={closeModal}>
          <div className="cf-modal" onClick={e => e.stopPropagation()}>
            {/* Dismantle */}
            {modal.kind === 'dismantle' && (
              <>
                <h3 className="cf-modal-title">Dismantle Item</h3>
                {modal.loading ? (
                  <p className="cf-modal-body">Loading preview…</p>
                ) : modal.preview ? (
                  <>
                    <p className="cf-modal-body">
                      Dismantling <strong>{modal.sourceItem.itemName}{qualityLabel(modal.sourceItem.qualityTier)}</strong> will destroy it and return:
                    </p>
                    <ul className="cf-modal-material-list">
                      {modal.preview.materials.map(m => (
                        <li key={m.materialId}><span className="cf-modal-qty">{m.quantity}x</span> {m.materialName}</li>
                      ))}
                    </ul>
                    <p className="cf-modal-warning">This action cannot be undone.</p>
                  </>
                ) : (
                  <p className="cf-modal-body cf-modal-warning">Could not load preview.</p>
                )}
              </>
            )}

            {/* Combine */}
            {modal.kind === 'combine' && (
              <>
                <h3 className="cf-modal-title">Combine Items</h3>
                <p className="cf-modal-body">
                  Combining two <strong>{modal.sourceItem.itemName}</strong> (+{modal.sourceItem.qualityTier}) creates one <strong>{modal.sourceItem.itemName} +{modal.sourceItem.qualityTier + 1}</strong> with 10% better stats. Both originals are consumed.
                </p>
                {combineCompatible.length === 0 ? (
                  <p className="cf-modal-warning">No compatible items in inventory. You need another {modal.sourceItem.itemName} at quality +{modal.sourceItem.qualityTier} that is not equipped.</p>
                ) : (
                  <>
                    <p className="cf-modal-label">Select a second item to combine with:</p>
                    <div className="cf-combine-list">
                      {combineCompatible.map(ci => (
                        <button
                          key={ci.id}
                          className={`cf-combine-item ${modal.combineTarget?.id === ci.id ? 'selected' : ''}`}
                          onClick={() => setModal(prev => prev ? { ...prev, combineTarget: ci } : null)}
                        >
                          <span className="cf-combine-item-name">{ci.itemName}{qualityLabel(ci.qualityTier)}</span>
                          <span className="cf-combine-item-date">{new Date(ci.craftedAt).toLocaleDateString()}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Donate */}
            {modal.kind === 'donate' && (
              <>
                <h3 className="cf-modal-title">Donate to Town</h3>
                {modal.loading ? (
                  <p className="cf-modal-body">Loading towns…</p>
                ) : modal.towns.length === 0 ? (
                  <p className="cf-modal-warning">No liberated towns available. Liberate a town first.</p>
                ) : (
                  <>
                    <p className="cf-modal-body">
                      Donating <strong>{modal.sourceItem.itemName}{qualityLabel(modal.sourceItem.qualityTier)}</strong> will grant the selected town <strong>+{computeStanding(modal.sourceItem)} standing</strong>.
                    </p>
                    <label className="cf-modal-label" htmlFor="donate-town-select">Choose a town:</label>
                    <select
                      id="donate-town-select"
                      className="cf-modal-select"
                      value={modal.selectedTownId ?? ''}
                      onChange={e => setModal(prev => prev ? { ...prev, selectedTownId: Number(e.target.value) } : null)}
                    >
                      {modal.towns.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name} — Level {t.level} ({t.standing}/1000 standing)
                        </option>
                      ))}
                    </select>
                    <p className="cf-modal-warning">This action cannot be undone.</p>
                  </>
                )}
              </>
            )}

            <div className="cf-modal-footer">
              <button className="cf-modal-btn cf-modal-btn--cancel" onClick={closeModal} disabled={busy}>
                Cancel
              </button>
              <button
                className={`cf-modal-btn cf-modal-btn--confirm cf-modal-btn--${modal.kind}`}
                onClick={handleConfirm}
                disabled={!confirmEnabled}
              >
                {busy ? 'Working…' : modal.kind === 'dismantle' ? 'Dismantle' : modal.kind === 'combine' ? 'Combine' : 'Donate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CraftingCraftedTab;
